// Read-only "shape of the dataset" hooks for the Browse page. Backed by the
// substrate-routed catalog + entity endpoints (NOT hand-rolled SPARQL), so
// Browse is backend-neutral: it works identically on RDF (Fuseki) and QROS,
// which speaks QQL, not SPARQL. The return shapes below are stable so the Browse
// UI is unaffected by the data source.
//
// Scope is a SET of graphs (GRAPH_SELECTION_SURFACES.md). Every endpoint here
// takes exactly one graph, so a multi-graph scope fans out — one request per
// graph — and the results are merged client-side. The all-graphs union is a
// single request against the union pseudo-graph, never N requests.

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useApiSession } from '@/lib/useApiSession';
import { scopeRequests } from '@/lib/useGraphScope';
import type { EntityResponse } from '@/lib/useEntity';
import type { TagView } from '@/lib/tags';

/** The combined shape of a fan-out read: merged rows plus the aggregate
 *  loading/error state, so callers keep the single-query ergonomics. */
export interface ScopedResult<T> {
  data: T[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

/** A combiner for a fan-out: merges the per-graph results once every request
 *  has resolved, and surfaces the first error rather than swallowing it.
 *  Passed to `useQueries` as `combine`, which re-runs it only when a result
 *  changes, so a re-render does not redo the merge and sort; each combiner is
 *  built once, at module level, because a new function would re-run it. */
function combiner<Row, Out>(merge: (rows: Row[]) => Out[]) {
  return (
    results: Array<{ data: Row | undefined; isLoading: boolean; error: Error | null }>,
  ): ScopedResult<Out> => {
    const error = results.find((r) => r.error)?.error ?? null;
    const settled = results.filter((r) => r.data !== undefined).map((r) => r.data as Row);
    return {
      data: settled.length === results.length && results.length > 0 ? merge(settled) : undefined,
      isLoading: results.some((r) => r.isLoading),
      error,
    };
  };
}

// --- Classes (with instance counts) ---

export interface ClassRow {
  uri: string;
  /** Resolved display label; empty when the term has none. The API resolves
   *  these in its own batch (catalog.rs `resolve_labels`) — they were being
   *  discarded here, which is why Browse could only render URIs. */
  label: string;
  instances: number;
}

interface CatalogClassesResponse {
  classes: Array<{ uri: string; label: string; count: number }>;
}

// A class declared in several graphs is one row; its instance count is the
// sum across the scope, which is what the same read against the union graph
// reports.
const combineClasses = combiner<ClassRow[], ClassRow>((pages) => {
  const byUri = new Map<string, ClassRow>();
  for (const page of pages) {
    for (const row of page) {
      const prev = byUri.get(row.uri);
      byUri.set(
        row.uri,
        prev
          ? {
              uri: row.uri,
              // Same class in two graphs: keep whichever graph named it.
              label: prev.label || row.label,
              instances: prev.instances + row.instances,
            }
          : row,
      );
    }
  }
  return [...byUri.values()].sort((a, b) => a.uri.localeCompare(b.uri));
});

export function useClasses(graphs: string[]): ScopedResult<ClassRow> {
  const { token, ready } = useApiSession();
  const targets = scopeRequests(graphs);

  return useQueries({
    queries: targets.map((graph) => ({
      queryKey: ['dataset', 'classes', graph, token ?? 'anon'],
      enabled: ready,
      staleTime: 60_000,
      queryFn: async (): Promise<ClassRow[]> => {
        const r = await apiFetch<CatalogClassesResponse>(
          `/api/v1/catalog/classes?graph=${encodeURIComponent(graph)}`,
          token,
        );
        if (!r.ok) throw new Error(r.message);
        return r.data.classes.map((c) => ({ uri: c.uri, label: c.label, instances: c.count }));
      },
    })),
    combine: combineClasses,
  });
}

// --- Predicates (with usage counts) ---

export interface PredicateRow {
  uri: string;
  /** Resolved display label; empty when the term has none. */
  label: string;
  uses: number;
}

interface CatalogPredicatesResponse {
  predicates: Array<{ uri: string; label: string; count: number }>;
}

const combinePredicates = combiner<PredicateRow[], PredicateRow>((pages) => {
  const byUri = new Map<string, PredicateRow>();
  for (const page of pages) {
    for (const row of page) {
      const prev = byUri.get(row.uri);
      byUri.set(
        row.uri,
        prev ? { uri: row.uri, label: prev.label || row.label, uses: prev.uses + row.uses } : row,
      );
    }
  }
  return [...byUri.values()].sort((a, b) => b.uses - a.uses || a.uri.localeCompare(b.uri));
});

export function usePredicates(graphs: string[]): ScopedResult<PredicateRow> {
  const { token, ready } = useApiSession();
  const targets = scopeRequests(graphs);

  return useQueries({
    queries: targets.map((graph) => ({
      queryKey: ['dataset', 'predicates', graph, token ?? 'anon'],
      enabled: ready,
      staleTime: 60_000,
      queryFn: async (): Promise<PredicateRow[]> => {
        const r = await apiFetch<CatalogPredicatesResponse>(
          `/api/v1/catalog/predicates?graph=${encodeURIComponent(graph)}`,
          token,
        );
        if (!r.ok) throw new Error(r.message);
        return r.data.predicates.map((p) => ({ uri: p.uri, label: p.label, uses: p.count }));
      },
    })),
    combine: combinePredicates,
  });
}

// --- Instances of a specific class ---

export interface InstanceRow {
  uri: string;
  /** Resolved display label; empty when the term has none. */
  label: string;
}

interface CatalogInstancesResponse {
  items: Array<{ uri: string; label: string }>;
  total: number;
}

/** Instances read per request: one page of the list. */
export const INSTANCE_PAGE_SIZE = 200;

interface InstancePage {
  rows: InstanceRow[];
  total: number;
}

/** The instances read so far, the number the scope holds and whether more
 *  pages remain. The number is the sum over the picked graphs, as a class's
 *  count on the Classes list is. */
export interface InstancesResult extends ScopedResult<InstanceRow> {
  total: number;
  hasMore: boolean;
  loadingMore: boolean;
}

/** Merges the pages of a fan-out laid out graph by graph, `pages` per graph.
 *  A page still loading ends its graph's rows, so the rows already shown stay
 *  in place while the next page arrives; the list waits only for each graph's
 *  first page. */
export function instancePagesCombiner(pages: number) {
  return (
    results: Array<{ data: InstancePage | undefined; isLoading: boolean; error: Error | null }>,
  ): InstancesResult => {
    const seen = new Set<string>();
    const rows: InstanceRow[] = [];
    let total = 0;
    let hasMore = false;
    let firstPending = false;
    for (let start = 0; start < results.length; start += pages) {
      const first = results[start]?.data;
      if (!first) {
        firstPending = true;
        continue;
      }
      total += first.total;
      let read = 0;
      for (const result of results.slice(start, start + pages)) {
        if (!result.data) break;
        read += result.data.rows.length;
        for (const row of result.data.rows) {
          if (seen.has(row.uri)) continue;
          seen.add(row.uri);
          rows.push(row);
        }
      }
      if (read < first.total) hasMore = true;
    }
    const loading = results.some((r) => r.isLoading);
    return {
      data: results.length > 0 && !firstPending ? rows : undefined,
      isLoading: firstPending && loading,
      loadingMore: !firstPending && loading,
      error: results.find((r) => r.error)?.error ?? null,
      total,
      hasMore,
    };
  };
}

/** The instances of a class in the scope, read `pages` pages deep. */
export function useInstancesOf(classUri: string | null, graphs: string[], pages = 1): InstancesResult {
  const { token, ready } = useApiSession();
  const targets = scopeRequests(graphs);
  const combine = useMemo(() => instancePagesCombiner(pages), [pages]);

  return useQueries({
    queries: targets.flatMap((graph) =>
      Array.from({ length: pages }, (_, page) => ({
        queryKey: ['dataset', 'instances', classUri ?? '_none', graph, page, token ?? 'anon'],
        enabled: ready && Boolean(classUri),
        staleTime: 60_000,
        queryFn: async (): Promise<InstancePage> => {
          if (!classUri) throw new Error('not ready');
          const params = new URLSearchParams({
            graph,
            class: classUri,
            offset: String(page * INSTANCE_PAGE_SIZE),
            limit: String(INSTANCE_PAGE_SIZE),
          });
          const r = await apiFetch<CatalogInstancesResponse>(
            `/api/v1/catalog/instances?${params.toString()}`,
            token,
          );
          if (!r.ok) throw new Error(r.message);
          return { rows: r.data.items.map((i) => ({ uri: i.uri, label: i.label })), total: r.data.total };
        },
      })),
    ),
    combine,
  });
}

interface CatalogLabelsResponse {
  labels: Record<string, string>;
}

/** Terms named in one labels request; the server takes no more. */
const LABEL_BATCH = 1000;

/** The label of each term that has one, read across the whole store whatever
 *  graphs are picked. Sent in batches the server accepts. */
export async function fetchLabels(
  uris: string[],
  token: string | undefined,
): Promise<{ ok: true; labels: Map<string, string> } | { ok: false; message: string }> {
  const labels = new Map<string, string>();
  for (let i = 0; i < uris.length; i += LABEL_BATCH) {
    const r = await apiFetch<CatalogLabelsResponse>('/api/v1/catalog/labels', token, {
      method: 'POST',
      body: JSON.stringify({ uris: uris.slice(i, i + LABEL_BATCH) }),
    });
    if (!r.ok) return { ok: false, message: r.message };
    for (const [uri, label] of Object.entries(r.data.labels)) labels.set(uri, label);
  }
  return { ok: true, labels };
}

// --- Triples about a specific subject ---

export interface SubjectTriple {
  predicate: string;
  object: string;
  objectType: string;
  objectLang: string | null;
  objectDatatype: string | null;
  /** Name of the rule that derived this fact (`@rule` tag), or null if asserted.
   * Rule-derived facts live in the inferred layer; `@layer` itself is stripped
   * server-side as structural, so the surviving `@rule` tag is the signal. */
  derivedByRule: string | null;
  /** The graph this statement was read from — the scope is a set, so a row
   *  without its origin would be unattributable. */
  graph: string;
}

/** The deriving rule's name from a fact's tags, or null when the fact is asserted. */
function derivedRuleFromTags(tags: TagView[] | undefined): string | null {
  const rule = tags?.find((t) => t.name === '@rule');
  if (!rule) return null;
  return rule.value.kind === 'literal' ? rule.value.value : null;
}

const combineSubjectTriples = combiner<SubjectTriple[], SubjectTriple>((pages) => {
  const seen = new Set<string>();
  const rows: SubjectTriple[] = [];
  for (const page of pages) {
    for (const t of page) {
      const key = `${t.predicate} ${t.objectType} ${t.object} ${t.objectLang ?? ''} ${t.objectDatatype ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(t);
    }
  }
  return rows;
});

export function useSubjectTriples(
  subject: string | null,
  graphs: string[],
): ScopedResult<SubjectTriple> {
  const { token, ready } = useApiSession();
  const targets = scopeRequests(graphs);

  return useQueries({
    queries: targets.map((graph) => ({
      queryKey: ['dataset', 'subject', subject ?? '_none', graph, token ?? 'anon'],
      enabled: ready && Boolean(subject),
      staleTime: 30_000,
      queryFn: async (): Promise<SubjectTriple[]> => {
        if (!subject) throw new Error('not ready');
        const r = await apiFetch<EntityResponse>(
          `/api/v1/entity?uri=${encodeURIComponent(subject)}&graph=${encodeURIComponent(graph)}`,
          token,
        );
        if (!r.ok) throw new Error(r.message);
        const triples: SubjectTriple[] = [];
        for (const group of r.data.outbound) {
          for (const v of group.values) {
            if (v.kind === 'literal') {
              triples.push({
                predicate: group.predicate.uri,
                object: v.value,
                objectType: 'literal',
                objectLang: v.lang ?? null,
                objectDatatype: v.datatype ?? null,
                derivedByRule: derivedRuleFromTags(v.tags),
                graph,
              });
            } else {
              triples.push({
                predicate: group.predicate.uri,
                object: v.uri,
                objectType: v.kind === 'bnode' ? 'bnode' : 'uri',
                objectLang: null,
                objectDatatype: null,
                derivedByRule: derivedRuleFromTags(v.tags),
                graph,
              });
            }
          }
        }
        return triples;
      },
    })),
    combine: combineSubjectTriples,
  });
}
